import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { getSceneByCreated } from "api/scene";
import { getCandidateProfile } from "api/dashboard/candidate/profile";
import { getResumeData } from "api/dashboard/parseResume";

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

export interface CandidateDataState {
  // Core Data
  scene: any | null;
  profile: any | null;
  resume: any | null;
  candidate: any | null;
  
  // Loading States
  loading: boolean;
  sceneLoading: boolean;
  profileLoading: boolean;
  resumeLoading: boolean;
  
  // Error States
  error: string | null;
  sceneError: string | null;
  profileError: string | null;
  resumeError: string | null;
  
  // Cache Management
  lastFetched: number | null;
  cacheExpiry: number; // Time in ms before cache is considered stale
  isCacheValid: boolean;
  
  // Metadata
  userId: string | null;
  userKey: string | null;
}

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState: CandidateDataState = {
  // Core Data
  scene: null,
  profile: null,
  resume: null,
  candidate: null,
  
  // Loading States
  loading: false,
  sceneLoading: false,
  profileLoading: false,
  resumeLoading: false,
  
  // Error States
  error: null,
  sceneError: null,
  profileError: null,
  resumeError: null,
  
  // Cache Management
  lastFetched: null,
  cacheExpiry: 5 * 60 * 1000, // 5 minutes default
  isCacheValid: false,
  
  // Metadata
  userId: null,
  userKey: null,
};

// ============================================================================
// ASYNC THUNKS
// ============================================================================

/**
 * Fetch all candidate data in parallel
 * This is the main entry point for getting all data at once
 */
export const fetchAllCandidateData = createAsyncThunk(
  "candidateDataCache/fetchAll",
  async (
    { userId, userKey, candidateId }: { userId: string; userKey: string; candidateId?: string },
    { rejectWithValue }
  ) => {
    try {
      const profileId = candidateId || userId;

      // PERFORMANCE: getCandidateProfile already fetches scene + resume on the backend
      // (via Promise.all in profile.js). Using it as the single source eliminates
      // 2 redundant API calls and ~4 duplicate DB queries.
      const profileResponse: any = profileId
        ? await getCandidateProfile(profileId)
        : null;

      // Extract all data from the profile response
      const profileData = profileResponse?.data || profileResponse;

      let profile = profileData?.profile || null;
      let scene = profileData?.scene || null;
      let resume = profileData?.resume || null;

      // Fallback: if the profile response didn't include scene/resume,
      // fetch them individually (backwards compatibility)
      if (!scene && !resume) {
        const resumeFetchId = candidateId || userKey;
        const [sceneResult, resumeResult] = await Promise.allSettled([
          getSceneByCreated(userKey),
          getResumeData(resumeFetchId),
        ]);

        if (!scene && sceneResult.status === "fulfilled") {
          scene = sceneResult.value?.data || null;
        }
        if (!resume && resumeResult.status === "fulfilled") {
          resume = resumeResult.value?.data?.data || resumeResult.value?.data || null;
        }
      }

      return {
        scene,
        profile,
        resume: resume || null,
        // Also pass the full candidate doc for profileCompletion etc.
        candidate: profileData?.candidate || null,
        timestamp: Date.now(),
      };
    } catch (error) {
      console.error("❌ [CandidateDataCache] Critical error fetching data:", error);
      return rejectWithValue(error?.message || "Failed to fetch candidate data");
    }
  }
);

/**
 * Fetch only scene data (for targeted updates)
 */
export const fetchSceneData = createAsyncThunk(
  "candidateDataCache/fetchScene",
  async (userKey: string, { rejectWithValue }) => {
    try {
      const response = await getSceneByCreated(userKey);
      return response?.data;
    } catch (error) {
      console.error("❌ [CandidateDataCache] Scene fetch failed:", error);
      return rejectWithValue(error?.message || "Failed to fetch scene data");
    }
  }
);

/**
 * Fetch only profile data (for targeted updates)
 */
export const fetchProfileData = createAsyncThunk(
  "candidateDataCache/fetchProfile",
  async (candidateId: string, { rejectWithValue }) => {
    try {
      const response = await getCandidateProfile(candidateId);
      return response?.profile || response?.data?.profile || null;
    } catch (error) {
      console.error("❌ [CandidateDataCache] Profile fetch failed:", error);
      return rejectWithValue(error?.message || "Failed to fetch profile data");
    }
  }
);

/**
 * Fetch only resume data (for targeted updates)
 */
export const fetchResumeData = createAsyncThunk(
  "candidateDataCache/fetchResume",
  async (resumeId: string, { rejectWithValue }) => {
    try {
      // resumeId can be candidateId or userKey (backwards compatible)
      const response = await getResumeData(resumeId);
      return response?.data?.data || response?.data;
    } catch (error) {
      console.error("❌ [CandidateDataCache] Resume fetch failed:", error);
      return rejectWithValue(error?.message || "Failed to fetch resume data");
    }
  }
);

// ============================================================================
// SLICE
// ============================================================================

export const candidateDataCacheSlice = createSlice({
  name: "candidateDataCache",
  initialState,
  reducers: {
    // Manual cache invalidation
    invalidateCache: (state) => {
      state.isCacheValid = false;
      state.lastFetched = null;
    },
    
    // Clear all cached data
    clearCache: (state) => {
      return initialState;
    },
    
    // Update cache expiry time
    setCacheExpiry: (state, action) => {
      state.cacheExpiry = action.payload;
    },
    
    // Partial update for specific sections (optimistic updates)
    updateScene: (state, action) => {
      state.scene = action.payload;
    },
    
    updateProfile: (state, action) => {
      state.profile = action.payload;
    },
    
    updateResume: (state, action) => {
      state.resume = action.payload;
    },
    
    // Reset error states
    clearErrors: (state) => {
      state.error = null;
      state.sceneError = null;
      state.profileError = null;
      state.resumeError = null;
    },
  },
  extraReducers: (builder) => {
    // ========================================================================
    // FETCH ALL DATA
    // ========================================================================
    builder
      .addCase(fetchAllCandidateData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllCandidateData.fulfilled, (state, action) => {
        state.loading = false;
        state.scene = action.payload.scene;
        state.profile = action.payload.profile;
        // Overwrite cached resume with server truth (null means delete any stale data)
        state.resume = action.payload.resume || null;
        state.candidate = action.payload.candidate || null;
        state.lastFetched = action.payload.timestamp;
        state.isCacheValid = true;
        state.userId = action.meta.arg.userId;
        state.userKey = action.meta.arg.userKey;
        state.error = null;
      })
      .addCase(fetchAllCandidateData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Unknown error";
        state.isCacheValid = false;
        console.error("❌ [CandidateDataCache] Failed to fetch all data");
      });

    // ========================================================================
    // FETCH SCENE DATA
    // ========================================================================
    builder
      .addCase(fetchSceneData.pending, (state) => {
        state.sceneLoading = true;
        state.sceneError = null;
      })
      .addCase(fetchSceneData.fulfilled, (state, action) => {
        state.sceneLoading = false;
        state.scene = action.payload;
        state.sceneError = null;
      })
      .addCase(fetchSceneData.rejected, (state, action) => {
        state.sceneLoading = false;
        state.sceneError = action.payload || "Unknown error";
      });

    // ========================================================================
    // FETCH PROFILE DATA
    // ========================================================================
    builder
      .addCase(fetchProfileData.pending, (state) => {
        state.profileLoading = true;
        state.profileError = null;
      })
      .addCase(fetchProfileData.fulfilled, (state, action) => {
        state.profileLoading = false;
        state.profile = action.payload;
        state.profileError = null;
      })
      .addCase(fetchProfileData.rejected, (state, action) => {
        state.profileLoading = false;
        state.profileError = action.payload || "Unknown error";
      });

    // ========================================================================
    // FETCH RESUME DATA
    // ========================================================================
    builder
      .addCase(fetchResumeData.pending, (state) => {
        state.resumeLoading = true;
        state.resumeError = null;
      })
      .addCase(fetchResumeData.fulfilled, (state, action) => {
        state.resumeLoading = false;
        state.resume = action.payload;
        state.resumeError = null;
      })
      .addCase(fetchResumeData.rejected, (state, action) => {
        state.resumeLoading = false;
        state.resumeError = action.payload || "Unknown error";
      });
  },
});

// ============================================================================
// ACTIONS & SELECTORS
// ============================================================================

export const {
  invalidateCache,
  clearCache,
  setCacheExpiry,
  updateScene,
  updateProfile,
  updateResume,
  clearErrors,
} = candidateDataCacheSlice.actions;

// Selectors
export const selectCandidateDataCache = (state) => state.candidateDataCacheReducer;
export const selectIsLoading = (state) => state.candidateDataCacheReducer?.loading;
export const selectSceneData = (state) => state.candidateDataCacheReducer?.scene;
export const selectProfileData = (state) => state.candidateDataCacheReducer?.profile;
export const selectResumeData = (state) => state.candidateDataCacheReducer?.resume;
export const selectCandidateDoc = (state) => state.candidateDataCacheReducer?.candidate;
export const selectIsCacheValid = (state) => state.candidateDataCacheReducer?.isCacheValid;
export const selectErrors = (state) => ({
  error: state.candidateDataCacheReducer?.error,
  sceneError: state.candidateDataCacheReducer?.sceneError,
  profileError: state.candidateDataCacheReducer?.profileError,
  resumeError: state.candidateDataCacheReducer?.resumeError,
});

// Helper to check if cache is stale
export const selectIsCacheStale = (state) => {
  const cache = state.candidateDataCacheReducer;
  if (!cache.lastFetched || !cache.isCacheValid) return true;
  const now = Date.now();
  const elapsed = now - cache.lastFetched;
  return elapsed > cache.cacheExpiry;
};

export default candidateDataCacheSlice.reducer;

